/**
 * Simple LRU (Least Recently Used) cache implementation
 * Evicts the least recently used item when the cache reaches maxSize
 *
 * @class LRUCache
 * @example
 * const cache = new LRUCache(100)
 * cache.set('key1', 'value1')
 * cache.get('key1') // returns 'value1'
 * cache.has('key1') // returns true
 */
export class LRUCache {
  /**
   * Creates an instance of LRUCache
   * @param {number} maxSize - Maximum number of items to store in the cache (default: 100)
   */
  constructor(maxSize = 100) {
    this.maxSize = maxSize
    this.cache = new Map()
  }

  /**
   * Check if a key exists in the cache
   * @param {*} key - The key to check
   * @returns {boolean} True if the key exists, false otherwise
   */
  has(key) {
    return this.cache.has(key)
  }

  /**
   * Get a value from the cache by key
   * Moves the item to the end (most recently used) when accessed
   * @param {*} key - The key to retrieve
   * @returns {*} The cached value, or undefined if not found
   */
  get(key) {
    if (!this.cache.has(key)) {
      return undefined
    }
    // Move to end (most recently used) by deleting and re-adding
    const value = this.cache.get(key)
    this.cache.delete(key)
    this.cache.set(key, value)
    return value
  }

  /**
   * Set a value in the cache
   * If the cache is at capacity, evicts the least recently used item
   * @param {*} key - The key to store
   * @param {*} value - The value to store
   */
  set(key, value) {
    // If key already exists, remove it first to update position
    if (this.cache.has(key)) {
      this.cache.delete(key)
    } else if (this.cache.size >= this.maxSize) {
      // Remove least recently used (first item in Map)
      const firstKey = this.cache.keys().next().value
      this.cache.delete(firstKey)
    }
    // Add to end (most recently used)
    this.cache.set(key, value)
  }

  /**
   * Clear all items from the cache
   */
  clear() {
    this.cache.clear()
  }

  /**
   * Get the current number of items in the cache
   * @returns {number} The size of the cache
   */
  get size() {
    return this.cache.size
  }
}
